import { Box, Flex } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IOption, IQuestionProps } from "../../../types";
import { Controller, useForm } from "react-hook-form";
import update from "immutability-helper";
import QuestionWrapper from "../../QuestionWrapper";
import RankItem from "./RankItem";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";

import Sort from "../../SVG/Sort";

const Ranking: React.FC<IQuestionProps> = ({
  defaultValue,
  onSubmit,
  onDirty,
  isHideSubmit,
  ...restProps
}) => {
  const { id, options, nextOnSelect, routeToQuestion } = restProps;

  const {
    handleSubmit,
    formState: { isDirty, isValid },
    control,
    trigger,
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      value: defaultValue,
    },
  });

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (!nextOnSelect && isDirty && onDirty) {
      onDirty();
    }
  }, [nextOnSelect, isDirty, onDirty]);

  const buttonSubmitRef = useRef<HTMLButtonElement>(null);

  const [answers, setAnswers] = useState<IOption[]>(options as IOption[]);

  const watchValue = watch("value");

  useEffect(() => {
    if (!defaultValue) {
      setValue(
        "value",
        options?.map((elm) => elm.value)
      );
    }
  }, [defaultValue, options, setValue]);

  useEffect(() => {
    if (watchValue)
      setAnswers((oldAnswers) =>
        (watchValue as string[]).map((v) => ({
          value: v,
          label: oldAnswers.find((elm) => elm.value === v)?.label || "",
        }))
      );
  }, [watchValue]);

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setAnswers((currentDatas) => {
      return update(currentDatas, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, currentDatas[dragIndex]],
        ],
      });
    });
  }, []);

  const onDrop = useCallback(() => {
    setValue(
      "value",
      answers.map((elm) => elm.value)
    );
  }, [answers, setValue]);

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  return (
    <form
      id={`question-${id}`}
      onSubmit={handleSubmit((formData) => {
        if (onSubmit) {
          onSubmit(formData.value as any[], { nextRoute: routeToQuestion });
        }
      })}
    >
      <QuestionWrapper
        isValid={isValid}
        submitButtonProps={{
          display: isHideSubmit ? "none" : "block",
          disabled: !isValid && !nextOnSelect,
          ref: buttonSubmitRef,
        }}
        {...restProps}
      >
        <Box
          border="1px solid #fff"
          borderRadius="10px"
          w="100%"
          maxW="446px"
          lineHeight={1.4}
          p={{ base: 5, lg: 4 }}
        >
          <Controller
            control={control}
            name="value"
            render={() => (
              <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
                {answers.map((option, index) => (
                  <RankItem
                    id={option.value}
                    key={option.value}
                    index={index}
                    moveCard={moveCard}
                    onDrop={onDrop}
                  >
                    {({ dragRef, previewRef, handlerId, isDragging }) => (
                      <Box
                        mb={index < answers.length - 1 ? 5 : 0}
                        ref={previewRef}
                        key={option.value}
                      >
                        <Flex
                          cursor="grab"
                          bgColor={isDragging ? "#dedede" : "#fff"}
                          borderRadius={{ base: "4px", lg: "10px" }}
                          data-handler-id={handlerId}
                          ref={dragRef}
                          _last={{
                            mb: 0,
                          }}
                          fontSize={{ base: "sm", md: "md" }}
                          minH="50px"
                          alignItems="center"
                          px={{ base: 3, lg: 4 }}
                        >
                          <Flex
                            justifyContent="center"
                            alignItems="center"
                            w="30px"
                            opacity={0.4}
                          >
                            {index + 1}
                          </Flex>
                          <Box p={1} flex={1}>
                            {option.label}
                          </Box>
                          <Flex
                            width="30px"
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Sort />
                          </Flex>
                        </Flex>
                      </Box>
                    )}
                  </RankItem>
                ))}
              </DndProvider>
            )}
          />
        </Box>
      </QuestionWrapper>
    </form>
  );
};

export default Ranking;
